import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { UsersRound, Save, Info } from "lucide-react";
import { api } from "@/lib/api";
import { Alert, Button, Card, FormField, PageContainer, PageHeader, Select } from "@/components/ui";

// ============================================================================
// Admin «Responsables por etapa» (RF-03): configura el usuario interno
// predeterminado de cada etapa del flujo, por sector. Al entrar una solicitud
// a la etapa, el sistema la auto-asigna a este usuario. Cambiarlo solo aplica
// hacia adelante (no reasigna lo ya asignado).
// ============================================================================

interface SectorOption { codigo: string; nombre: string; }
interface ResponsableDefault {
  estadoCodigo: string;
  estadoNombre: string;
  orden: number;
  usuarioId: string | null;
  usuarioNombre: string | null;
  usuarioCorreo: string | null;
}
interface UsuarioAsignable { id: string; nombreCompleto: string; correo: string; }

export function ResponsablesEtapaPage() {
  const qc = useQueryClient();
  const [sector, setSector] = useState("TELECOM");
  // estadoCodigo → usuarioId ("" = sin predeterminado) capturado en pantalla.
  const [seleccion, setSeleccion] = useState<Record<string, string>>({});
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState<{ tipo: "ok" | "err"; texto: string } | null>(null);

  const qSectores = useQuery({
    queryKey: ["catalogos-sectores"],
    queryFn: async () => (await api.get<SectorOption[]>("/api/catalogos/sectores")).data,
    staleTime: 5 * 60 * 1000,
  });

  const qDefaults = useQuery({
    queryKey: ["asignacion-defaults", sector],
    queryFn: async () =>
      (await api.get<ResponsableDefault[]>(`/api/asignaciones/defaults?sector=${sector}`)).data,
    enabled: !!sector,
  });

  const qAsignables = useQuery({
    queryKey: ["asignables", sector],
    queryFn: async () =>
      (await api.get<UsuarioAsignable[]>(`/api/asignaciones/asignables?sector=${sector}`)).data,
    enabled: !!sector,
    staleTime: 60_000,
  });

  // Sincroniza la captura con lo persistido cada vez que cambia el sector o llega data.
  useEffect(() => {
    if (!qDefaults.data) return;
    const next: Record<string, string> = {};
    for (const d of qDefaults.data) next[d.estadoCodigo] = d.usuarioId ?? "";
    setSeleccion(next);
    setMensaje(null);
  }, [qDefaults.data]);

  const etapas = qDefaults.data ?? [];
  const hayCambios = etapas.some((d) => (d.usuarioId ?? "") !== (seleccion[d.estadoCodigo] ?? ""));

  const guardar = async () => {
    setGuardando(true);
    setMensaje(null);
    try {
      const cambios = etapas
        .filter((d) => (d.usuarioId ?? "") !== (seleccion[d.estadoCodigo] ?? ""))
        .map((d) => ({
          estadoCodigo: d.estadoCodigo,
          usuarioId: seleccion[d.estadoCodigo] || null,
        }));
      await api.put(`/api/asignaciones/defaults?sector=${sector}`, cambios);
      await qc.invalidateQueries({ queryKey: ["asignacion-defaults", sector] });
      setMensaje({ tipo: "ok", texto: "Responsables predeterminados guardados." });
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } }).response?.data?.detail ||
        (e as Error).message || "Error inesperado.";
      setMensaje({ tipo: "err", texto: detail });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        icon={<UsersRound className="h-5 w-5" />}
        title="Responsables por etapa"
        subtitle="Usuario interno que recibe automáticamente las solicitudes al entrar a cada etapa del flujo, por sector."
      />

      <Card padding="sm">
        <div className="flex flex-wrap items-end gap-3">
          <FormField label="Sector">
            <Select value={sector} onChange={(e) => setSector(e.target.value)}>
              {(qSectores.data ?? [{ codigo: "TELECOM", nombre: "Telecomunicaciones" }]).map((s) => (
                <option key={s.codigo} value={s.codigo}>{s.nombre}</option>
              ))}
            </Select>
          </FormField>
          <div className="flex items-start gap-2 rounded bg-primary-tint p-2.5 text-xs text-fg-muted">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary-strong" />
            <span>
              La auto-asignación aplica a las solicitudes que <strong>entren</strong> a la etapa a
              partir de ahora; no reasigna lo ya asignado. Una etapa sin predeterminado deja las
              solicitudes «Sin asignar» (el flujo no se detiene).
            </span>
          </div>
        </div>
      </Card>

      <Card padding="none">
        {qDefaults.isLoading ? (
          <div className="p-6 text-sm text-fg-subtle">Cargando etapas…</div>
        ) : qDefaults.error ? (
          <div className="p-4"><Alert variant="warning">Error cargando la configuración del sector.</Alert></div>
        ) : etapas.length === 0 ? (
          <div className="p-6 text-sm text-fg-subtle">
            Este sector no tiene un flujo activo configurado; no hay etapas que asignar.
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-surface-2">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-fg-subtle">Etapa</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-fg-subtle">Responsable predeterminado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-edge bg-surface">
                {etapas.map((d) => (
                  <tr key={d.estadoCodigo}>
                    <td className="px-4 py-3 align-middle">
                      <div className="font-medium text-fg">{d.estadoNombre}</div>
                      <div className="font-mono text-xs text-fg-subtle">{d.estadoCodigo}</div>
                    </td>
                    <td className="px-4 py-2 align-middle">
                      <Select
                        value={seleccion[d.estadoCodigo] ?? ""}
                        onChange={(e) =>
                          setSeleccion((prev) => ({ ...prev, [d.estadoCodigo]: e.target.value }))
                        }
                        className="max-w-md"
                      >
                        <option value="">(Sin predeterminado — queda «Sin asignar»)</option>
                        {(qAsignables.data ?? []).map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.nombreCompleto} ({u.correo})
                          </option>
                        ))}
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-edge/60 px-4 py-3">
          <div className="text-xs">
            {mensaje && (
              <span className={mensaje.tipo === "ok" ? "text-estado-ok" : "text-estado-err"}>
                {mensaje.texto}
              </span>
            )}
          </div>
          <Button
            icon={<Save className="h-4 w-4" />}
            onClick={guardar}
            disabled={!hayCambios || guardando}
          >
            {guardando ? "Guardando…" : "Guardar cambios"}
          </Button>
        </div>
      </Card>
    </PageContainer>
  );
}
